/**
 * Centralized API Configuration
 * 
 * Single source of truth for all API-related configuration.
 * Uses environment variables to determine the correct API URL.
 * 
 * IMPORTANT: Never hardcode API URLs in the application.
 * Always import and use getApiBaseUrl() or API_CONFIG.
 */

/**
 * Get the API base URL from environment variables
 * Falls back to localhost development server if not set
 * @returns {string} The API base URL (without trailing slash)
 */
export const getApiBaseUrl = () => {
  const url = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';
  // Remove trailing slash if present
  return url.endsWith('/') ? url.slice(0, -1) : url;
};

/**
 * Get the current environment
 * @returns {string} The environment name (development, staging, production)
 */
export const getEnvironment = () => {
  return process.env.REACT_APP_ENV || 'development';
};

/**
 * Check if running in development mode
 * @returns {boolean}
 */
export const isDevelopment = () => {
  return getEnvironment() === 'development';
};

/**
 * Check if running in production mode
 * @returns {boolean}
 */
export const isProduction = () => {
  return getEnvironment() === 'production';
};

/**
 * API Configuration Object
 * Used by axios client for request configuration
 */
export const API_CONFIG = {
  baseURL: getApiBaseUrl(),
  timeout: 30000, // 30 seconds
  headers: {
    'Content-Type': 'application/json',
  },
};

/**
 * API Endpoint Constants
 * Centralized endpoint paths for easy maintenance
 */
export const API_ENDPOINTS = {
  // Authentication
  AUTH_LOGIN: '/auth/login',
  AUTH_LOGOUT: '/auth/logout',
  AUTH_REFRESH: '/auth/refresh',
  AUTH_ME: '/auth/me',
  AUTH_FORGOT_PASSWORD: '/auth/forgot-password',
  AUTH_RESET_PASSWORD: '/auth/reset-password',
  
  // Orders
  ORDERS: '/orders',
  ORDERS_BY_ID: (id) => `/orders/${id}`,
  ORDERS_CONFIRM: (id) => `/orders/${id}/confirm`,
  ORDERS_PREPARE: (id) => `/orders/${id}/prepare`,
  ORDERS_READY: (id) => `/orders/${id}/ready`,
  ORDERS_PENDING: '/orders/pending',
  ORDERS_BY_MERCHANT: (merchantId) => `/orders/by-merchant/${merchantId}`,
  
  // Products & Inventory
  PRODUCTS: '/products',
  PRODUCTS_BY_ID: (id) => `/products/${id}`,
  PRODUCTS_SEARCH: '/products/search',
  PRODUCT_MERCHANTS: '/products/product-merchants',
  PRODUCT_MERCHANTS_BY_ID: (id) => `/products/product-merchants/${id}`,
  PRODUCTS_BY_MERCHANT: (merchantId) => `/products/merchants/${merchantId}/products`,
  
  // Merchants
  MERCHANTS: '/merchants',
  MERCHANTS_BY_ID: (id) => `/merchants/${id}`,
  MERCHANTS_LOGO_UPLOAD: (id) => `/merchants/${id}/logo`,
  MERCHANTS_LOGO_DELETE: (id) => `/merchants/${id}/logo`,
  
  // Categories
  CATEGORIES: '/categories',
  CATEGORIES_BY_ID: (id) => `/categories/${id}`,
  
  // Brands
  BRANDS: '/brands',
  BRANDS_BY_ID: (id) => `/brands/${id}`,
  
  // Dashboard
  DASHBOARD_METRICS: '/dashboard/metrics',
  DASHBOARD_INVENTORY: '/dashboard/inventory',
  DASHBOARD_TOP_PRODUCTS: '/dashboard/top-products',
  DASHBOARD_SALES_BY_CATEGORY: '/dashboard/sales-by-category',
  
  // Users
  USERS: '/users',
  USERS_BY_ID: (id) => `/users/${id}`,
  USERS_ME: '/users/me',
  
  // Images
  IMAGES_UPLOAD: '/images/upload',

  // Aggregated Orders
  AGGREGATED_ORDERS_BY_MERCHANT: (merchantId) => `/aggregated-orders/by-merchant/${merchantId}`,
  AGGREGATED_ORDERS_BY_ID: (id) => `/aggregated-orders/${id}`,
  AGGREGATED_ORDERS_CONFIRM: (id) => `/aggregated-orders/${id}/confirm`,
};

/**
 * HTTP Status Codes
 */
export const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  NO_CONTENT: 204,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  CONFLICT: 409,
  INTERNAL_SERVER_ERROR: 500,
  SERVICE_UNAVAILABLE: 503,
};

/**
 * Request timeout configurations for different operation types
 */
export const TIMEOUTS = {
  DEFAULT: 30000,      // 30 seconds
  UPLOAD: 60000,       // 60 seconds for file uploads
  LONG_RUNNING: 90000, // 90 seconds for reports/exports
};

export default API_CONFIG;
